import {isEmpty, some} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {billingPlatformApi, userApi} from 'api';
import {
    Button, EmailLink, InformationModal, Wizard,
    ChargebeePaymentStep, PaymentSuccessStep, MaxUsersStep, TotalPrice, SelectUsersToKeepStep, DataWrapper
} from 'components';
import {SelectExistingPaymentSourcesStep} from 'components/SelectExistingPaymentSourcesStep/SelectExistingPaymentSourcesStep';
import {APP_SUPPORT_EMAIL} from 'constants/app';
import {CHARGEBEE_CLIENT_SCRIPT, VALID_PAYMENT_SOURCE_STATUSES} from 'constants/billing-platform';
import {ACCOUNT_BLOCKED} from 'constants/images';
import {AccountBlockedCode, AccountStateName} from 'enums';
import {useApiData, useExternalScript, useObservable, usePreventWindowUnload} from 'hooks';
import {BaseLayout} from 'layouts/Base';
import {TRANSLATION_KEYS} from 'locale';
import {authDataStore} from 'services';
import {BillingPlatformSubscription} from 'types';
import {sessionUtils, userAuthUtils} from 'utils';

enum PageState {
    Initial = 'initial',
    NewCard = 'new_card'
}

const T_KEYS = TRANSLATION_KEYS.ACCOUNT_BLOCKED_PAGE;
const T_KEYS_WIZARD_NAV = TRANSLATION_KEYS.COMPONENTS.BILLING_PLATFORM_WIZARD_NAV;

const ACCOUNT_BLOCKED_CODE_LABEL = {
    [AccountBlockedCode.RegularPaymentFailed]: T_KEYS.REASONS.REGULAR_PAYMENT_FAILED,
    [AccountBlockedCode.InitialPaymentTimeoutExpired]: T_KEYS.REASONS.INITIAL_PAYMENT_TIMEOUT_EXPIRED,
    [AccountBlockedCode.SubscriptionPaused]: T_KEYS.REASONS.SUBSCRIPTION_PAUSED
};

const CODES_TO_REACTIVATE = [
    AccountBlockedCode.InitialPaymentTimeoutExpired,
    AccountBlockedCode.RegularPaymentFailed,
    AccountBlockedCode.SubscriptionPaused
];

export const AccountBlocked: React.FC = () => {
    const {t} = useTranslation();
    const {localUser} = useObservable(authDataStore);
    const canManage = useMemo(() => userAuthUtils.isAccountAdmin(), []);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [pageState, setPageState] = useState<PageState>(PageState.Initial);
    const [maxUsers, setMaxUsers] = useState(1);
    const [showRemoveUsersStep, setShowRemoveUsersStep] = useState(true);
    const [usersToRemove, setUsersToRemove] = useState<string[]>([]);

    const [wizardInstance, setWizardInstance] = useState(null);
    const paymentCompleted = wizardInstance && wizardInstance.currentStep === wizardInstance.totalSteps;
    usePreventWindowUnload(pageState === PageState.NewCard && !paymentCompleted);

    const {
        send: fetchBrowserCredentials,
        data: credentials,
        loading: credentialsLoading,
        error: credentialsError,
        reset: resetBrowserCredentials
    } = useApiData(billingPlatformApi.getBrowserCredentials);

    const {
        send: fetchUsers,
        data: users,
        loading: fetchUsersLoading,
        error: fetchUsersError
    } = useApiData(userApi.getUsersSummary);

    const {loaded: scriptLoaded} = useExternalScript(CHARGEBEE_CLIENT_SCRIPT);

    const startJourney = useCallback(() => {
        window.location.reload();
    }, []);

    const handleReactivateSuccess = useCallback(() => {
        setSuccessModalOpen(true);
    }, []);
    const {send: reactivate, loading: reactivateLoading, error} = useApiData(billingPlatformApi.reactivateSubscription, {
        onSuccess: handleReactivateSuccess
    });

    const {send: fetchBillingPlatformData, data: platformData} = useApiData(billingPlatformApi.getBillingPlatformData);

    const blockedWithReactivatingCode = useMemo(() => localUser.accountStateName === AccountStateName.Blocked &&
        CODES_TO_REACTIVATE.includes(localUser.accountStateData?.code), [localUser]);
    const {unitPrice, currency, billingPeriodUnit, quantity} = useMemo(() =>
        platformData?.subscription || {} as BillingPlatformSubscription, [platformData]);
    const hasValidCards = useMemo(() => !isEmpty(platformData?.paymentSources) &&
        some(platformData?.paymentSources, ({status}) => VALID_PAYMENT_SOURCE_STATUSES.includes(status)), [platformData]);

    const initialPaymentTimeoutExpired = useMemo(() =>
        localUser.accountStateData?.code === AccountBlockedCode.InitialPaymentTimeoutExpired, [localUser]);
    const canReactivate = useMemo(() =>
        canManage && platformData?.customer && blockedWithReactivatingCode, [canManage, platformData, blockedWithReactivatingCode]);

    const maxUsersFooterLabel = useMemo(() => (
        <div>{`${t(T_KEYS.ACCOUNT_USERS)}: ${users?.length}`}</div>
    ), [users, t]);

    const customWizardFooterLabel = useMemo(() => (
        <TotalPrice
            className='sm:tw-mr-4 -sm:tw-w-full -sm:tw-text-right'
            unitPrice={unitPrice}
            maxUsers={maxUsers}
            periodUnit={billingPeriodUnit}
            currency={currency}
        />
    ), [currency, maxUsers, unitPrice, billingPeriodUnit]);

    const navItems = useMemo(() => [
        t(T_KEYS_WIZARD_NAV.MAX_USERS),
        ...(showRemoveUsersStep ? [t(T_KEYS_WIZARD_NAV.SELECT_USERS)] : []),
        ...(hasValidCards ? [t(T_KEYS_WIZARD_NAV.CARD_SELECT)] : []),
        t(T_KEYS_WIZARD_NAV.CARD_INFO),
        t(T_KEYS_WIZARD_NAV.DONE)
    ], [showRemoveUsersStep, hasValidCards, t]);

    const handleReactivate = useCallback(() => {
        if (initialPaymentTimeoutExpired) {
            reactivate({});
        } else {
            setPageState(PageState.NewCard);
        }
    }, [reactivate, initialPaymentTimeoutExpired]);

    useEffect(() => {
        fetchBillingPlatformData({short: true});
    }, [fetchBillingPlatformData]);

    useEffect(() => {
        if (scriptLoaded && pageState === PageState.NewCard) {
            fetchBrowserCredentials();
            fetchUsers();
            return () => {
                resetBrowserCredentials();
            };
        }
    }, [scriptLoaded, pageState, fetchUsers, fetchBrowserCredentials, resetBrowserCredentials]);

    useEffect(() => {
        if (localUser.accountStateName === AccountStateName.Active) {
            startJourney();
        }
    }, [localUser, startJourney]);

    useEffect(() => {
        if (quantity) {
            setMaxUsers(quantity);
        }
    }, [quantity]);

    useEffect(() => {
        setShowRemoveUsersStep(users?.length && users.length > maxUsers);
    }, [users, maxUsers]);

    return (
        <BaseLayout hasRestrictedProfileMenu hasFooter hasLogo>
            <div className='tw-bg-layout tw-h-full'>
                <div className='tw-absolute tw-top-20 tw-bottom-12 tw-left-0 tw-right-0 tw-flex-center'>
                    <div className='tw-bg-light tw-p-8 tw-shadow-md tw-text-center tw-max-h-full tw-overflow-y-auto'>
                        {pageState === PageState.Initial &&
                            <Fragment>
                                <div className='tw-ts-h3-bold tw-text-alert'>{t(T_KEYS.TITLE)}</div>
                                <img
                                    src={ACCOUNT_BLOCKED}
                                    className='tw-mx-auto tw-my-12 tw-max-w-64'
                                    alt={t(T_KEYS.TITLE)}
                                />
                                <div className='tw-font-bold tw-mb-2'>
                                    {`${t(T_KEYS.GREETING)} ${sessionUtils.getParticipantName(localUser)}`}
                                </div>
                                <div className='tw-mb-2'>{t(T_KEYS.DESCRIPTION)}</div>
                                {localUser.accountStateData &&
                                    <div>
                                        <span className='tw-font-bold'>{`${t(T_KEYS.REASON_LABEL)}: `}</span>
                                        {t(ACCOUNT_BLOCKED_CODE_LABEL[localUser.accountStateData.code])}
                                        {localUser.accountStateData.message &&
                                            <div className='tw-text-default'>{localUser.accountStateData.message}</div>}
                                    </div>}
                                {canReactivate &&
                                    <div className='tw-mt-4'>
                                        <Button
                                            loading={reactivateLoading}
                                            color='primary'
                                            icon='refresh'
                                            onClick={handleReactivate}
                                        >
                                            {t(T_KEYS.REACTIVATE.LABEL)}
                                        </Button>
                                    </div>}
                                {error && <div className='tw-mt-4 tw-text-alert'>{t(T_KEYS.ERROR_MESSAGE)}</div>}
                                <div className='tw-mt-4'>{t(T_KEYS.CONTACT_LABEL)}</div>
                                <EmailLink email={APP_SUPPORT_EMAIL} />
                            </Fragment>}

                        {pageState === PageState.NewCard &&
                            <DataWrapper
                                hideContentOnLoading={false}
                                isLoading={fetchUsersLoading || credentialsLoading}
                                isError={!!credentialsError || !!fetchUsersError}
                            >
                                <div className='tw-text-left'>
                                    <Wizard instance={setWizardInstance} navItems={navItems}>
                                        <MaxUsersStep
                                            maxUsers={maxUsers}
                                            unitPrice={unitPrice}
                                            currency={currency}
                                            periodUnit={billingPeriodUnit}
                                            wizardFooterProps={{
                                                hasNext: true,
                                                children: maxUsersFooterLabel
                                            }}
                                            onUpdate={setMaxUsers}
                                        />
                                        {showRemoveUsersStep &&
                                            <SelectUsersToKeepStep
                                                usersToRemove={usersToRemove}
                                                users={users}
                                                localUser={localUser}
                                                maxUsers={maxUsers}
                                                wizardFooterProps={{
                                                    hasNext: true,
                                                    hasPrevious: true
                                                }}
                                                onSubmit={setUsersToRemove}
                                            />}
                                        {hasValidCards &&
                                            <SelectExistingPaymentSourcesStep
                                                maxUsers={maxUsers}
                                                usersToRemove={usersToRemove}
                                                paymentSources={platformData?.paymentSources}
                                                selectedPaymentSourceId={platformData?.customer.primaryPaymentSourceId}
                                                wizardFooterProps={{
                                                    hasNext: true,
                                                    hasPrevious: true,
                                                    children: customWizardFooterLabel
                                                }}
                                                existingPaymentCallback={billingPlatformApi.reactivateSubscription}
                                            />}
                                        <ChargebeePaymentStep
                                            credentials={credentials}
                                            maxUsers={maxUsers}
                                            unitPrice={unitPrice}
                                            currency={currency}
                                            periodUnit={billingPeriodUnit}
                                            usersToRemove={usersToRemove}
                                            wizardFooterProps={{
                                                hasNext: true,
                                                hasPrevious: true,
                                                children: customWizardFooterLabel
                                            }}
                                            paymentAddedCallback={billingPlatformApi.reactivateSubscription}
                                        />
                                        <PaymentSuccessStep action={startJourney} />
                                    </Wizard>
                                </div>
                            </DataWrapper>}
                    </div>
                </div>
            </div>
            <InformationModal fullWidth maxWidth='xs' title={t(T_KEYS.SUCCESS_MODAL.TITLE)} open={successModalOpen} onClose={startJourney}>
                <div className='tw-ts-h5 tw-text-center'>{t(T_KEYS.SUCCESS_MODAL.DESCRIPTION)}</div>
                <div className='tw-text-center tw-mt-8'>
                    <Button onClick={startJourney}>{t(T_KEYS.SUCCESS_MODAL.PROCEED)}</Button>
                </div>
            </InformationModal>
        </BaseLayout>
    );
};